import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Shield, Search, ChevronLeft, ChevronRight, Clock, User, Activity, AlertTriangle, Trash2, Send, Plus, X, Check, Eye, Calendar } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const eventBadgeConfig = {
  PUBLICACION_CREADA: { label: 'Nueva Publicación', variant: 'default', icon: Plus },
  PUBLICACION_ENVIADA: { label: 'Publicación Enviada', variant: 'default', icon: Send },
  OFERTA_ACEPTADA: { label: 'Oferta Aceptada', variant: 'default', icon: Check },
  OFERTA_RECHAZADA: { label: 'Oferta Rechazada', variant: 'destructive', icon: X },
  ENTIDAD_ELIMINADA: { label: 'Eliminación', variant: 'destructive', icon: Trash2 },
  USUARIO_CREADO: { label: 'Usuario Creado', variant: 'outline', icon: User },
  USUARIO_ELIMINADO: { label: 'Usuario Eliminado', variant: 'destructive', icon: User },
  CLIENTE_CREADO: { label: 'Cliente Creado', variant: 'outline', icon: User },
  OFERTA_INGRESADA: { label: 'Oferta Ingresada', variant: 'secondary', icon: Plus },
};

const methodColors = {
  GET: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  POST: 'bg-blue-500/15 text-blue-700 dark:text-blue-400',
  PUT: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  PATCH: 'bg-orange-500/15 text-orange-700 dark:text-orange-400',
  DELETE: 'bg-red-500/15 text-red-700 dark:text-red-400',
};

const Auditoria = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.isSuperAdmin === true;

  // Eventos state
  const [eventos, setEventos] = useState([]);
  const [eventosTotal, setEventosTotal] = useState(0);
  const [eventosPage, setEventosPage] = useState(1);
  const [eventosPages, setEventosPages] = useState(1);
  const [eventosLoading, setEventosLoading] = useState(true);
  const [eventoTipoFilter, setEventoTipoFilter] = useState('all');
  const [eventoTipos, setEventoTipos] = useState([]);
  const [eventoFechaDesde, setEventoFechaDesde] = useState('');
  const [eventoFechaHasta, setEventoFechaHasta] = useState('');

  // Requests state
  const [requests, setRequests] = useState([]);
  const [requestsTotal, setRequestsTotal] = useState(0);
  const [requestsPage, setRequestsPage] = useState(1);
  const [requestsPages, setRequestsPages] = useState(1);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [requestMetodoFilter, setRequestMetodoFilter] = useState('all');
  const [requestUriSearch, setRequestUriSearch] = useState('');
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [requestFechaDesde, setRequestFechaDesde] = useState('');
  const [requestFechaHasta, setRequestFechaHasta] = useState('');

  const LIMIT = 30;

  const loadEventos = useCallback(async () => {
    setEventosLoading(true);
    try {
      const params = { page: eventosPage, limit: LIMIT };
      if (eventoTipoFilter !== 'all') params.tipo = eventoTipoFilter;
      if (eventoFechaDesde) params.fecha_desde = new Date(eventoFechaDesde).toISOString();
      if (eventoFechaHasta) {
        const hasta = new Date(eventoFechaHasta);
        hasta.setHours(23, 59, 59, 999);
        params.fecha_hasta = hasta.toISOString();
      }
      const res = await axios.get(`${API}/auditoria/eventos`, { params });
      setEventos(res.data.items);
      setEventosTotal(res.data.total);
      setEventosPages(res.data.pages);
    } catch (error) {
      console.error('Error loading audit events:', error);
    } finally {
      setEventosLoading(false);
    }
  }, [eventosPage, eventoTipoFilter, eventoFechaDesde, eventoFechaHasta]);

  const loadEventoTipos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auditoria/eventos/tipos`);
      setEventoTipos(res.data);
    } catch (error) {
      console.error('Error loading event types:', error);
    }
  }, []);

  const loadRequests = useCallback(async () => {
    setRequestsLoading(true);
    try {
      const params = { page: requestsPage, limit: LIMIT };
      if (requestMetodoFilter !== 'all') params.metodo = requestMetodoFilter;
      if (requestUriSearch) params.uri = requestUriSearch;
      if (requestFechaDesde) params.fecha_desde = new Date(requestFechaDesde).toISOString();
      if (requestFechaHasta) {
        const hasta = new Date(requestFechaHasta);
        hasta.setHours(23, 59, 59, 999);
        params.fecha_hasta = hasta.toISOString();
      }
      const res = await axios.get(`${API}/auditoria/requests`, { params });
      setRequests(res.data.items);
      setRequestsTotal(res.data.total);
      setRequestsPages(res.data.pages);
    } catch (error) {
      console.error('Error loading audit requests:', error);
    } finally {
      setRequestsLoading(false);
    }
  }, [requestsPage, requestMetodoFilter, requestUriSearch, requestFechaDesde, requestFechaHasta]);


  useEffect(() => {
    loadEventos();
  }, [loadEventos]);

  useEffect(() => {
    loadEventoTipos();
  }, [loadEventoTipos]);

  useEffect(() => {
    if (isSuperAdmin) loadRequests();
  }, [isSuperAdmin, loadRequests]);

  const formatDate = (isoStr) => {
    if (!isoStr) return '-';
    return new Date(isoStr).toLocaleString('es-UY', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  const Pagination = ({ page, pages, total, setPage }) => (
    <div className="flex items-center justify-between px-4 py-3 border-t">
      <span className="text-sm text-muted-foreground">
        {total} resultado{total !== 1 ? 's' : ''}
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={page <= 1}
          onClick={() => setPage(page - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm">
          {page} / {pages}
        </span>
        <Button
          variant="outline"
          size="sm"
          disabled={page >= pages}
          onClick={() => setPage(page + 1)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  const getStatusBadge = (code) => {
    if (code >= 200 && code < 300) return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400';
    if (code >= 300 && code < 400) return 'bg-blue-500/15 text-blue-700 dark:text-blue-400';
    if (code >= 400 && code < 500) return 'bg-amber-500/15 text-amber-700 dark:text-amber-400';
    return 'bg-red-500/15 text-red-700 dark:text-red-400';
  };

  return (
    <div className="space-y-6 bg-background min-h-screen">
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">Auditoría</h1>
              <p className="text-muted-foreground mt-1">Registro inmutable de eventos del sistema</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Tabs defaultValue="eventos" className="space-y-6">
          <TabsList>
            <TabsTrigger value="eventos" className="gap-2">
              <Activity className="h-4 w-4" />
              Eventos
            </TabsTrigger>
            {isSuperAdmin && (
              <TabsTrigger value="requests" className="gap-2">
                <Eye className="h-4 w-4" />
                Requests
              </TabsTrigger>
            )}
          </TabsList>

          {/* ====== EVENTOS TAB ====== */}
          <TabsContent value="eventos" className="space-y-4">
            <Card className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <Select value={eventoTipoFilter} onValueChange={(v) => { setEventoTipoFilter(v); setEventosPage(1); }}>
                  <SelectTrigger className="w-full sm:w-[260px]">
                    <SelectValue placeholder="Tipo de evento" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los tipos</SelectItem>
                    {eventoTipos.map((tipo) => (
                      <SelectItem key={tipo} value={tipo}>
                        {eventBadgeConfig[tipo]?.label || tipo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <Input
                    type="date"
                    value={eventoFechaDesde}
                    onChange={(e) => { setEventoFechaDesde(e.target.value); setEventosPage(1); }}
                    className="w-[160px]"
                    placeholder="Desde"
                  />
                  <span className="text-muted-foreground text-sm">—</span>
                  <Input
                    type="date"
                    value={eventoFechaHasta}
                    onChange={(e) => { setEventoFechaHasta(e.target.value); setEventosPage(1); }}
                    className="w-[160px]"
                    placeholder="Hasta"
                  />
                </div>
              </div>
            </Card>

            <Card>
              {eventosLoading ? (
                <div className="flex items-center justify-center p-12">
                  <div className="text-muted-foreground">Cargando...</div>
                </div>
              ) : eventos.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-12 gap-3">
                  <Activity className="h-12 w-12 text-muted-foreground" />
                  <p className="text-muted-foreground">No hay eventos registrados</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="border-b">
                        <tr>
                          <th className="text-left p-4 font-medium">Fecha</th>
                          <th className="text-left p-4 font-medium">Tipo</th>
                          <th className="text-left p-4 font-medium">Usuario</th>
                          <th className="text-left p-4 font-medium">Descripción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eventos.map((evento, idx) => {
                          const config = eventBadgeConfig[evento.tipo] || {};
                          const Icon = config.icon || AlertTriangle;
                          return (
                            <tr key={idx} className="border-b last:border-0 hover:bg-accent/50">
                              <td className="p-4 text-sm text-muted-foreground whitespace-nowrap">
                                <div className="flex items-center gap-1.5">
                                  <Clock className="h-3.5 w-3.5" />
                                  {formatDate(evento.timestamp)}
                                </div>
                              </td>
                              <td className="p-4">
                                <Badge variant={config.variant || 'secondary'} className="gap-1">
                                  <Icon className="h-3 w-3" />
                                  {config.label || evento.tipo}
                                </Badge>
                              </td>
                              <td className="p-4 text-sm">
                                <div className="flex items-center gap-1.5">
                                  <User className="h-3.5 w-3.5 text-muted-foreground" />
                                  {evento.usuario?.nombre || 'Sistema'}
                                </div>
                              </td>
                              <td className="p-4 text-sm max-w-md">
                                {evento.descripcion}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    page={eventosPage}
                    pages={eventosPages}
                    total={eventosTotal}
                    setPage={setEventosPage}
                  />
                </>
              )}
            </Card>
          </TabsContent>

          {/* ====== REQUESTS TAB ====== */}
          {isSuperAdmin && (
            <TabsContent value="requests" className="space-y-4">
              <Card className="p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar por URI..."
                      value={requestUriSearch}
                      onChange={(e) => setRequestUriSearch(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { setRequestsPage(1); loadRequests(); } }}
                      className="pl-10"
                    />
                  </div>
                  <Select value={requestMetodoFilter} onValueChange={(v) => { setRequestMetodoFilter(v); setRequestsPage(1); }}>
                    <SelectTrigger className="w-full sm:w-[160px]">
                      <SelectValue placeholder="Método" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="GET">GET</SelectItem>
                      <SelectItem value="POST">POST</SelectItem>
                      <SelectItem value="PUT">PUT</SelectItem>
                      <SelectItem value="PATCH">PATCH</SelectItem>
                      <SelectItem value="DELETE">DELETE</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <Input
                      type="date"
                      value={requestFechaDesde}
                      onChange={(e) => { setRequestFechaDesde(e.target.value); setRequestsPage(1); }}
                      className="w-[160px]"
                    />
                    <span className="text-muted-foreground text-sm">—</span>
                    <Input
                      type="date"
                      value={requestFechaHasta}
                      onChange={(e) => { setRequestFechaHasta(e.target.value); setRequestsPage(1); }}
                      className="w-[160px]"
                    />
                  </div>
                </div>
              </Card>

              <Card>
                {requestsLoading ? (
                  <div className="flex items-center justify-center p-12">
                    <div className="text-muted-foreground">Cargando...</div>
                  </div>
                ) : requests.length === 0 ? (
                  <div className="flex flex-col items-center justify-center p-12 gap-3">
                    <Eye className="h-12 w-12 text-muted-foreground" />
                    <p className="text-muted-foreground">No hay requests registrados</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="border-b">
                          <tr>
                            <th className="text-left p-4 font-medium">Fecha</th>
                            <th className="text-left p-4 font-medium">Método</th>
                            <th className="text-left p-4 font-medium">URI</th>
                            <th className="text-left p-4 font-medium">Usuario</th>
                            <th className="text-left p-4 font-medium">Status</th>
                            <th className="text-right p-4 font-medium">Duración</th>
                          </tr>
                        </thead>
                        <tbody>
                          {requests.map((req, idx) => (
                            <>
                              <tr
                                key={idx}
                                className="border-b last:border-0 hover:bg-accent/50 cursor-pointer"
                                onClick={() => setExpandedRequest(expandedRequest === idx ? null : idx)}
                              >
                                <td className="p-4 text-sm text-muted-foreground whitespace-nowrap">
                                  {formatDate(req.timestamp)}
                                </td>
                                <td className="p-4">
                                  <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${methodColors[req.metodo] || ''}`}>
                                    {req.metodo}
                                  </span>
                                </td>
                                <td className="p-4 text-sm font-mono max-w-xs truncate" title={req.uri}>
                                  {req.uri}
                                </td>
                                <td className="p-4 text-sm text-muted-foreground">
                                  {req.usuario?.email || '-'}
                                </td>
                                <td className="p-4">
                                  <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${getStatusBadge(req.status_code)}`}>
                                    {req.status_code}
                                  </span>
                                </td>
                                <td className="p-4 text-sm text-right text-muted-foreground">
                                  {req.duracion_ms}ms
                                </td>
                              </tr>
                              {expandedRequest === idx && (
                                <tr key={`detail-${idx}`} className="border-b bg-muted/30">
                                  <td colSpan={6} className="p-4">
                                    <div className="grid sm:grid-cols-2 gap-4 text-sm">
                                      <div>
                                        <span className="font-medium">IP: </span>
                                        <span className="text-muted-foreground">{req.ip || '-'}</span>
                                      </div>
                                      <div>
                                        <span className="font-medium">User Agent: </span>
                                        <span className="text-muted-foreground text-xs">{req.user_agent || '-'}</span>
                                      </div>
                                      {req.query && (
                                        <div className="sm:col-span-2">
                                          <span className="font-medium">Query: </span>
                                          <span className="text-muted-foreground font-mono text-xs">{req.query}</span>
                                        </div>
                                      )}
                                      {req.body && (
                                        <div className="sm:col-span-2">
                                          <span className="font-medium">Body: </span>
                                          <pre className="mt-1 p-2 bg-muted rounded-md text-xs overflow-x-auto">
                                            {typeof req.body === 'object' ? JSON.stringify(req.body, null, 2) : req.body}
                                          </pre>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <Pagination
                      page={requestsPage}
                      pages={requestsPages}
                      total={requestsTotal}
                      setPage={setRequestsPage}
                    />
                  </>
                )}
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default Auditoria;
